import {
  AS400_EXPORT_WORKSHEET_COLUMNS,
  as400ColumnEditKey,
  as400ColumnMaxLengths,
  as400ColumnNumberFormats,
} from '@erp/rpc';
import type {
  As400ExportColumn,
  As400ExportGroup,
  As400NumberFormat,
} from '@erp/rpc';
import {
  asWarning,
  maxLengthRule,
  numberFormatRule,
  onlyIn,
  skipsWhitespaceOnlyWarn,
  slashCountRule,
  whitespaceOnlyRule,
  type AddRowDefault,
  type CellAutoFill,
  type CellRow,
  type CellRule,
  type CellViolation,
  type WorksheetRules,
} from './as400CellRuleKinds';
import {
  itemNumberCollisionRule,
  itemNumberFormatRule,
} from './sheetRules/itemNumberRules';
import { ITEM_SHEET_RULES } from './sheetRules/itemSheetRules';
import { PURCHASE_SHEET_RULES } from './sheetRules/purchaseSheetRules';
import { PRICE_SHEET_RULES } from './sheetRules/priceSheetRules';
import { ITEM_ADD_SHEET_RULES } from './sheetRules/itemAddSheetRules';
import { STOCK_SHEET_RULES } from './sheetRules/stockSheetRules';
import { STOCK_ADD_SHEET_RULES } from './sheetRules/stockAddSheetRules';
import { ITEM_KEYWORD_SHEET_RULES } from './sheetRules/itemKeywordSheetRules';
import { MATRIX_SHEET_RULES } from './sheetRules/matrixSheetRules';
import type { ItemAddLookup } from './itemAddRules';
import type { StockUomLookup } from './stockUomRules';

/**
 * Cell-level validation for the review grid (GEN-2748): rules a value is
 * checked against as it is typed (the open editor, see useEditorCheck), once
 * it is committed, and again for every unsaved edit on Save — never rules
 * that block keystrokes. A value that breaks one is kept (typing one character
 * of a two-character code is a valid moment on the way to a valid value),
 * marked on the grid and explained, and Save refuses it until it's fixed —
 * unless the rule is only a warning, which Save lets through.
 *
 * Pure: the grid, the save path and the error summary all read violations
 * through {@link cellViolations} / {@link rowViolations}, each with its own
 * {@link CellRow} over the values it holds. The kinds of rule are in
 * as400CellRuleKinds; each worksheet's own rules, auto-fills and add-row
 * defaults in sheetRules/, composed here.
 */

/** Each worksheet's own rules (see sheetRules/). */
const WORKSHEET_RULES: Record<As400ExportGroup, WorksheetRules> = {
  ITEM: ITEM_SHEET_RULES,
  PURCHASE_DATA: PURCHASE_SHEET_RULES,
  PRICE: PRICE_SHEET_RULES,
  ITEM_ADD: ITEM_ADD_SHEET_RULES,
  STOCK: STOCK_SHEET_RULES,
  STOCK_ADD: STOCK_ADD_SHEET_RULES,
  ITEM_KW: ITEM_KEYWORD_SHEET_RULES,
  MATRIX_CLASSIFICATION: MATRIX_SHEET_RULES,
};

export interface CellRuleOptions {
  takenItemNumbers?: ReadonlySet<string>;
}

export type CellRowLookups = {
  itemAdds?: ItemAddLookup;
  stockUoms?: StockUomLookup;
};

/** A view's rules, grouped by the field whose cell they mark. */
export type CellRulesByField = ReadonlyMap<string, readonly CellRule[]>;

/** No rules at all — a view whose columns declare none. */
export const NO_CELL_RULES: CellRulesByField = new Map();

/** Group rules by the field they mark. */
export function groupCellRules(rules: readonly CellRule[]): CellRulesByField {
  const byField = new Map<string, CellRule[]>();
  for (const rule of rules) {
    const list = byField.get(rule.field);
    if (list) list.push(rule);
    else byField.set(rule.field, [rule]);
  }
  return byField;
}

/**
 * The worksheet `columns` are, when they are one: a worksheet grid's columns
 * are its contract columns (behind the CDB key), the same objects. A Queries
 * view's raw columns are no worksheet's.
 */
function worksheetOf(
  columns: readonly As400ExportColumn[],
): As400ExportGroup | undefined {
  const shown = new Set(columns);
  const groups = Object.keys(WORKSHEET_RULES) as As400ExportGroup[];
  return groups.find((group) => {
    const [first] = AS400_EXPORT_WORKSHEET_COLUMNS[group];
    return first !== undefined && shown.has(first);
  });
}

// Every worksheet column's number format, by field: the first worksheet to
// declare a field decides it.
const WORKSHEET_NUMBER_FORMATS: ReadonlyMap<string, As400NumberFormat> =
  new Map(
    Object.values(AS400_EXPORT_WORKSHEET_COLUMNS)
      .flatMap((columns) => [...as400ColumnNumberFormats(columns)])
      .reverse(),
  );

/**
 * Each column's number format: what it declares, else the format a worksheet
 * gives the same field. The fallback is for the Queries views' raw columns,
 * which are bare field names, so a raw price still refuses `1,234`.
 */
function numberFormatsOf(
  columns: readonly As400ExportColumn[],
): Map<string, As400NumberFormat> {
  const formats = new Map(as400ColumnNumberFormats(columns));
  for (const column of columns) {
    const field = as400ColumnEditKey(column);
    const known = field && WORKSHEET_NUMBER_FORMATS.get(field.toUpperCase());
    if (field && known && !formats.has(field)) formats.set(field, known);
  }
  return formats;
}

function blankMarkerRules(columns: readonly As400ExportColumn[]): CellRule[] {
  const rules: CellRule[] = [];
  for (const column of columns) {
    const field = as400ColumnEditKey(column);
    const marker = column.blankMarker;
    if (!field || !marker) continue;
    rules.push(asWarning(slashCountRule(field, marker.length)));
    if (!skipsWhitespaceOnlyWarn(field)) {
      rules.push(asWarning(whitespaceOnlyRule(field)));
    }
  }
  return rules;
}

/**
 * What the columns carry: character limits and number formats. A field whose
 * sheet rule enforces its limit itself (`replacesColumnLimit`) gets no generic
 * limit check, so the cell isn't flagged twice.
 */
function hasItemNumberColumn(columns: readonly As400ExportColumn[]): boolean {
  return columns.some((column) => as400ColumnEditKey(column) === 'ITEMNO');
}

function itemNumberRules(taken: ReadonlySet<string>): CellRule[] {
  return [
    itemNumberFormatRule(),
    asWarning(onlyIn('add', itemNumberCollisionRule(taken))),
  ];
}

function columnRules(
  columns: readonly As400ExportColumn[],
  sheetRules: readonly CellRule[],
  taken: ReadonlySet<string>,
): CellRule[] {
  const ownLimits = new Set<string>();
  for (const rule of sheetRules) {
    if (rule.replacesColumnLimit) ownLimits.add(rule.field);
  }
  const limitRules: CellRule[] = [];
  for (const [field, limit] of as400ColumnMaxLengths(columns)) {
    if (!ownLimits.has(field)) limitRules.push(maxLengthRule(field, limit));
  }
  return [
    ...limitRules,
    ...[...numberFormatsOf(columns)].map(([field, format]) =>
      numberFormatRule(field, format),
    ),
    ...blankMarkerRules(columns),
    ...(hasItemNumberColumn(columns) ? itemNumberRules(taken) : []),
  ];
}

/**
 * The rules a view's columns carry — each column's character limit (GEN-2723)
 * and number format (GEN-2726) — plus, on a worksheet, that worksheet's own
 * rules. A raw column of the Queries views takes the number format its field
 * has on a worksheet, and nothing else.
 */
export function as400CellRules(
  columns: readonly As400ExportColumn[],
  options?: CellRuleOptions,
): CellRulesByField {
  const group = worksheetOf(columns);
  const sheetRules = group ? WORKSHEET_RULES[group].rules : [];
  const taken = options?.takenItemNumbers ?? new Set<string>();
  return groupCellRules([
    ...columnRules(columns, sheetRules, taken),
    ...sheetRules,
  ]);
}

/** The auto-fills a view's grid runs on every batch of edits. */
export function as400CellAutoFills(
  columns: readonly As400ExportColumn[],
): readonly CellAutoFill[] {
  const group = worksheetOf(columns);
  return group ? WORKSHEET_RULES[group].autoFills : [];
}

/** The values a row added to `group`'s worksheet starts with. */
export function as400AddRowDefaults(
  group: As400ExportGroup,
): readonly AddRowDefault[] {
  return WORKSHEET_RULES[group].addDefaults;
}

/**
 * Whether the rule is checked on this row: the author touched the rule's cell
 * or anything it reads, and the rule applies to the row's mode.
 */
function ruleInPlay(rule: CellRule, row: CellRow): boolean {
  if (rule.modes && !rule.modes.includes(row.mode)) return false;
  if (rule.evenWhenUnedited) return true;
  return row.edited(rule.field) || (rule.dependsOn ?? []).some(row.edited);
}

/**
 * The violations on one cell. Only rules the author is in play for are
 * checked — the cell, or a field its rule reads, carries an unsaved edit — so
 * a review never flags data it didn't change.
 */
export function cellViolations(
  rules: CellRulesByField,
  field: string,
  row: CellRow,
): CellViolation[] {
  const violations: CellViolation[] = [];
  for (const rule of rules.get(field) ?? []) {
    if (!ruleInPlay(rule, row)) continue;
    const value = row.value(field);
    const detail = rule.check(value, row);
    if (!detail) continue;
    violations.push({
      field,
      value,
      severity: rule.severity ?? 'error',
      detail,
    });
  }
  return violations;
}

/** Whether a violation stops Save (an error), rather than a warning. */
export function isBlocking(violation: CellViolation): boolean {
  return violation.severity === 'error';
}

/** The violations on every ruled cell of a row, in rule order. */
export function rowViolations(
  rules: CellRulesByField,
  row: CellRow,
): CellViolation[] {
  return [...rules.keys()].flatMap((field) =>
    cellViolations(rules, field, row),
  );
}

/**
 * The fields to re-check after `changed` was edited: the field itself and
 * every field whose rule reads it.
 */
export function fieldsAffectedBy(
  rules: CellRulesByField,
  changed: string,
): string[] {
  const fields: string[] = [];
  for (const [field, fieldRules] of rules) {
    if (
      field === changed ||
      fieldRules.some((rule) => rule.dependsOn?.includes(changed))
    ) {
      fields.push(field);
    }
  }
  return fields;
}

function itemRowKeys(
  rows: ReadonlyMap<string, CellRow>,
): ReadonlyMap<string, readonly string[]> {
  const keysByItem = new Map<string, string[]>();
  for (const [key, row] of rows) {
    const item = row.value('ITEMNO');
    if (item === null) continue;
    const keys = keysByItem.get(item);
    if (keys) keys.push(key);
    else keysByItem.set(item, [key]);
  }
  return keysByItem;
}

function siblingRowsOf(
  rows: ReadonlyMap<string, CellRow>,
  key: string,
  peerKeys: readonly string[],
): CellRow[] {
  const siblings: CellRow[] = [];
  for (const peer of peerKeys) {
    if (peer === key) continue;
    const row = rows.get(peer);
    if (row) siblings.push(row);
  }
  return siblings;
}

export function attachItemSiblings(
  rows: ReadonlyMap<string, CellRow>,
): ReadonlyMap<string, CellRow> {
  const keysByItem = itemRowKeys(rows);
  const next = new Map<string, CellRow>();
  for (const [key, row] of rows) {
    const item = row.value('ITEMNO');
    const peerKeys = item === null ? undefined : keysByItem.get(item);
    next.set(
      key,
      peerKeys && peerKeys.length > 1
        ? { ...row, siblings: siblingRowsOf(rows, key, peerKeys) }
        : row,
    );
  }
  return next;
}
